/**
 * Performance Optimization Utilities
 *
 * Collection of utilities to improve web performance:
 * - Resource prefetching
 * - Route preloading
 * - Performance monitoring
 * - Web Vitals tracking
 */

/**
 * Prefetch a route component
 * Useful for preloading likely next pages
 */
export const prefetchRoute = (routePath: string) => {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = routePath;
  link.as = 'script';
  document.head.appendChild(link);
};

/**
 * Preload critical resources
 */
export const preloadResource = (
  href: string,
  as: 'script' | 'style' | 'image' | 'font' | 'fetch'
) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = href;
  link.as = as;
  if (as === 'font') {
    link.crossOrigin = 'anonymous';
  }
  document.head.appendChild(link);
};

/**
 * Debounce function for performance
 */
export function debounce<T extends (...args: never[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function for performance
 */
export function throttle<T extends (...args: never[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Check if browser supports WebP format
 */
export const supportsWebP = (): Promise<boolean> => {
  if (typeof window === 'undefined') return Promise.resolve(false);

  return new Promise((resolve) => {
    const webP = new Image();
    webP.onload = webP.onerror = () => {
      resolve(webP.height === 2);
    };
    webP.src =
      'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
  });
};

/**
 * Get image format to use (webp if supported, otherwise original)
 */
export const getOptimalImageFormat = async (
  originalSrc: string
): Promise<string> => {
  const webpSupported = await supportsWebP();

  if (webpSupported && !originalSrc.endsWith('.svg')) {
    // Replace extension with .webp if available
    // In production, you'd have actual webp versions
    return originalSrc;
  }

  return originalSrc;
};

/**
 * Lazy load images that are about to enter viewport
 */
export const setupImageLazyLoading = () => {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const dataSrc = img.getAttribute('data-src');
          if (dataSrc) {
            img.src = dataSrc;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach((img) => {
      imageObserver.observe(img);
    });
  }
};

/**
 * Report Web Vitals to analytics
 * Integrates with Google Analytics if available
 */
export const reportWebVitals = (metric: {
  name: string;
  value: number;
  id: string;
  rating: 'good' | 'needs-improvement' | 'poor';
}) => {
  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${metric.name}:`, metric.value, metric.rating);
  }

  // Send to Google Analytics if available
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', metric.name, {
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      event_category: 'Web Vitals',
      event_label: metric.id,
      non_interaction: true,
    });
  }
};

/**
 * Measure and log performance metrics using modern Performance API
 */
export const measurePerformance = () => {
  if (typeof window === 'undefined' || !window.performance) return;

  // Wait for page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (navigation) {
        const pageLoadTime = navigation.loadEventEnd - navigation.fetchStart;
        const connectTime = navigation.responseEnd - navigation.requestStart;
        const renderTime = navigation.domComplete - navigation.domInteractive;

        if (import.meta.env.DEV) {
          console.table({
            'Page Load Time': `${Math.round(pageLoadTime)}ms`,
            'Connect Time': `${Math.round(connectTime)}ms`,
            'Render Time': `${Math.round(renderTime)}ms`,
          });
        }

        // Report to analytics
        if (window.gtag) {
          window.gtag('event', 'timing_complete', {
            name: 'page_load',
            value: Math.round(pageLoadTime),
            event_category: 'Performance',
          });
        }
      }
    }, 0);
  });
};

/**
 * Request idle callback wrapper with fallback
 */
export const requestIdleCallback =
  typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? window.requestIdleCallback
    : (callback: IdleRequestCallback) => {
        const start = Date.now();
        return setTimeout(() => {
          callback({
            didTimeout: false,
            timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
          });
        }, 1) as unknown as number;
      };

/**
 * Cancel idle callback wrapper
 */
export const cancelIdleCallback =
  typeof window !== 'undefined' && 'cancelIdleCallback' in window
    ? window.cancelIdleCallback
    : (id: number) => {
        clearTimeout(id);
      };

/**
 * Defer non-critical JavaScript
 * Executes callback during browser idle time
 */
export const deferExecution = (callback: () => void) => {
  if (typeof window === 'undefined') {
    callback();
    return;
  }

  requestIdleCallback(callback);
};

/**
 * Batch DOM reads and writes for better performance
 */
class DOMScheduler {
  private readCallbacks: (() => void)[] = [];
  private writeCallbacks: (() => void)[] = [];
  private scheduled = false;

  scheduleRead(callback: () => void) {
    this.readCallbacks.push(callback);
    this.schedule();
  }

  scheduleWrite(callback: () => void) {
    this.writeCallbacks.push(callback);
    this.schedule();
  }

  private schedule() {
    if (this.scheduled) return;
    this.scheduled = true;

    requestAnimationFrame(() => {
      // Execute all reads first
      const reads = this.readCallbacks.splice(0);
      reads.forEach((callback) => callback());

      // Then execute all writes
      const writes = this.writeCallbacks.splice(0);
      writes.forEach((callback) => callback());

      this.scheduled = false;
    });
  }
}

export const domScheduler = new DOMScheduler();

/**
 * Check if reduced motion is preferred
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Initialize all performance optimizations
 */
export const initPerformanceOptimizations = () => {
  // Measure performance
  measurePerformance();

  // Setup image lazy loading
  deferExecution(() => {
    setupImageLazyLoading();
  });

  // Prefetch likely routes (can be customized based on analytics)
  deferExecution(() => {
    // Example: Prefetch product pages from homepage
    if (window.location.pathname === '/') {
      prefetchRoute('/opendq');
      prefetchRoute('/scient-cloud');
    }
  });
};

export default {
  prefetchRoute,
  preloadResource,
  debounce,
  throttle,
  supportsWebP,
  getOptimalImageFormat,
  reportWebVitals,
  measurePerformance,
  deferExecution,
  domScheduler,
  prefersReducedMotion,
  initPerformanceOptimizations,
};
